// PublicRamsPage — unauthenticated secure-link surface for a RAMS request
// (token-scoped, no login). Mirrors PublicFormPage.jsx's conventions but for
// the RAMS questionnaire runtime (Phase 3).
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertCircle, ShieldCheck, HardHat } from "lucide-react";
import RamsRuntimeForm from "@/components/rams/RamsRuntimeForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Center({ children }) {
    return <div className="min-h-screen bg-[#F5F3F0] grid place-items-center p-4">{children}</div>;
}

export default function PublicRamsPage() {
    const { token } = useParams();
    const [view, setView] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        axios.get(`${API}/public/rams/${token}`)
            .then((r) => setView(r.data))
            .catch((e) => setError(e.response?.status === 410 ? e.response.data.detail
                : e.response?.status === 404 ? "This link is invalid or has expired." : "Could not load this questionnaire."))
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) return <Center><Loader2 className="animate-spin" size={28} /></Center>;
    if (error) return <Center><div className="text-center max-w-sm" data-testid="public-rams-error"><AlertCircle className="mx-auto mb-2 text-red-500" size={28} /><p className="text-[#6F6A64]">{error}</p></div></Center>;

    const brand = view.branding?.primary_colour || "#C75A1B";

    return (
        <div className="min-h-screen bg-[#F5F3F0] py-6 px-4" data-testid="public-rams-page">
            <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-sm border border-[#E5E0DA] p-5">
                <div className="flex items-center gap-2.5 mb-5 pb-4 border-b border-[#F0EDE8]">
                    <div className="w-9 h-9 rounded-lg grid place-items-center text-white" style={{ background: brand }}><HardHat size={18} /></div>
                    <div>
                        <div className="text-[13px] font-bold text-[#1F1F1F]">{view.client_name || "RAMS Questionnaire"}</div>
                        <div className="text-[11px] text-[#9A968F]">{[view.project_name, view.site_name].filter(Boolean).join(" · ")}</div>
                    </div>
                </div>
                <div className="mb-4">
                    <h1 className="text-lg font-bold text-[#1F1F1F]">{view.request.title || view.request.template_name}</h1>
                    <p className="text-[12px] text-[#9A968F] flex items-center gap-1 mt-0.5"><ShieldCheck size={11} /> Ref {view.request.reference}{view.request.due_date ? ` · Due ${view.request.due_date}` : ""}</p>
                </div>
                {!view.editable && (
                    <div className="mb-4 p-3 rounded-lg bg-[#F5F3F0] text-[12.5px] text-[#6F6A64]">
                        This questionnaire has already been submitted ({view.request.status}). It is now read-only.
                    </div>
                )}
                <RamsRuntimeForm
                    sections={view.sections} initialAnswers={view.answers} initialFiles={view.files} prefill={view.prefill}
                    editable={view.editable} requestMeta={view.request} guestMode brand={brand}
                    saveApi={(body) => axios.post(`${API}/public/rams/${token}/save`, body).then((r) => r.data)}
                    submitApi={(body) => axios.post(`${API}/public/rams/${token}/submit`, body).then((r) => r.data)}
                    uploadApi={(fieldKey, file) => {
                        const fd = new FormData(); fd.append("field_key", fieldKey); fd.append("file", file);
                        return axios.post(`${API}/public/rams/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
                    }}
                />
            </div>
        </div>
    );
}
